import React, { useState } from 'react';
import { Input, Modal, Spin, Checkbox, DatePicker, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTransferForm } from './hooks/useTransferForm';
import { pw9204f1Api } from '../../services/pw9204f1Api';
import LookupField from '../../components/LookupField';
import { SharedActionBar } from '../../components/SharedActionBar';
import type { ApprovalState } from '../../components/SharedActionBar';
import ApprovalHistoryModal from './components/ApprovalHistoryModal';
import { useModulePermission } from '../../hooks/usePermission';
import dayjs from 'dayjs';

const PW9204F1Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const perm = useModulePermission('pw9204f1');
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    empId, setEmpId, desc, setDesc,
    oldDept, setOldDept, oldTitle, setOldTitle, oldGrade, setOldGrade,
    hireDate, setHireDate,
    newDept, setNewDept, newTitle, setNewTitle, newGrade, setNewGrade,
    effDate, setEffDate,
    notifyOldMgr, setNotifyOldMgr,
    details, addDetailRow, removeDetailRow, updateDetailRow,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch, loadRecords,
  } = useTransferForm();

  const [searchText, setSearchText] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  // Load review record if ?review= is present
  React.useEffect(() => {
    if (reviewId) {
      quickSearch(reviewId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId]);

  // Approval state derived from f90f_80
  // ⚠️ 'V' 是規格法定送審旗標（FoxPro 新增/存檔即寫 V），非廢碼非沖銷碼！
  // N=草稿/退回/沖銷、V=送審中、P=已核准（批次 5，使用者裁決 2026-06-12）
  const statusCode = (current?.f90f_80 || '').trim();
  const approval: ApprovalState = {
    statusCode,
    isApproved: statusCode === 'P',
    isPending: statusCode === 'V',
    isLocked: ['P', 'V'].includes(statusCode),
  };

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除異動單號「${current.f90f_01}」？`,
      okText: '刪除', okType: 'danger', cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleSubmit = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將異動單 ${current.f90f_01} 送審？`,
      okText: '送審', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9204f1Api.submitForApproval(current.f90f_01);
          message.success(resp.message || '送審成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '送審失敗');
        }
      },
    });
  };

  const handleApprove = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准異動單 ${current.f90f_01}？`,
      okText: '核准', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9204f1Api.approve(current.f90f_01);
          message.success(resp.message || '核准成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '核准失敗');
        }
      },
    });
  };

  const handleReject = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回異動單 ${current.f90f_01}？`,
      okText: '退回', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9204f1Api.reject(current.f90f_01);
          message.success(resp.message || '退回成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '退回失敗');
        }
      },
    });
  };

  const handleWriteoff = () => {
    if (!current || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${current.f90f_01}？沖銷後可重新送審。`,
      okText: '沖銷', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await pw9204f1Api.writeoff(current.f90f_01);
          message.success(resp.message || '沖銷成功');
          await loadRecords();
        } catch (err: any) {
          message.error(err?.response?.data?.error || '沖銷失敗');
        }
      },
    });
  };

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6" style={{ paddingBottom: 80 }}>
        <h2 className="text-xl font-bold mb-4">
          人事異動單 (pw9204f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽'}]
            {records.length > 0 ? ` ${currentIdx + 1} / ${records.length}` : ' 無資料'}
          </span>
        </h2>

        {/* Search */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="單號或員工代號" value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && quickSearch(searchText.trim())}
            disabled={isEditing} style={{ width: 260 }} />
          <button className="sab-btn" onClick={() => searchText.trim() && quickSearch(searchText.trim())} disabled={isEditing}>查詢</button>
        </div>

        {/* Header fields */}
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">異動單號</label>
            <Input value={current?.f90f_01 || ''} disabled style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              員工代號 <span className="text-red-500">*</span>
            </label>
            <LookupField lookupType="employee" value={empId as string || ''} onChange={(code) => setEmpId(code)} disabled={!isEditing} title="搜尋員工" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">入單日期</label>
            <DatePicker value={current?.f90f_12 ? dayjs(current.f90f_12) : null}
              disabled style={{ width: '100%' }} format="YYYY.MM.DD" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">入單人員</label>
            <Input value={current?.f90f_15 || ''} disabled />
          </div>
        </div>

        {/* Dept / Title / Grade rows */}
        <div className="grid grid-cols-4 gap-4 mb-2">
          <div className="font-medium text-sm text-gray-500 pt-6"></div>
          <div className="text-sm font-medium text-gray-500 text-center">部門</div>
          <div className="text-sm font-medium text-gray-500 text-center">職稱</div>
          <div className="text-sm font-medium text-gray-500 text-center">職等</div>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-2">
          <div className="text-sm font-medium text-gray-700 pt-1">原任</div>
          <LookupField lookupType="department" value={oldDept as string || ''} onChange={(code) => setOldDept(code)} disabled={!isEditing} title="搜尋部門" />
          <LookupField lookupType="position" value={oldTitle as string || ''} onChange={(code) => setOldTitle(code)} disabled={!isEditing} title="搜尋職稱" />
          <LookupField lookupType="salaryTier" value={oldGrade as string || ''} onChange={(code) => setOldGrade(code)} disabled={!isEditing} title="搜尋職等" />
        </div>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="text-sm font-medium text-gray-700 pt-1">
            新任 <span className="text-red-500">*</span>
          </div>
          <LookupField lookupType="department" value={newDept as string || ''} onChange={(code) => setNewDept(code)} disabled={!isEditing} title="搜尋部門" />
          <LookupField lookupType="position" value={newTitle as string || ''} onChange={(code) => setNewTitle(code)} disabled={!isEditing} title="搜尋職稱" />
          <LookupField lookupType="salaryTier" value={newGrade as string || ''} onChange={(code) => setNewGrade(code)} disabled={!isEditing} title="搜尋職等" />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">到職日</label>
            <DatePicker value={hireDate ? dayjs(hireDate) : null}
              onChange={(d) => setHireDate(d ? d.toISOString() : null)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              擬生效日 <span className="text-red-500">*</span>
            </label>
            <DatePicker value={effDate ? dayjs(effDate) : null}
              onChange={(d) => setEffDate(d ? d.toISOString() : null)}
              disabled={!isEditing} style={{ width: '100%' }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">實際生效日</label>
            <DatePicker value={current?.f90f_39 ? dayjs(current.f90f_39) : null}
              disabled style={{ width: '100%' }} />
          </div>
        </div>

        <div className="mb-4">
          <Checkbox checked={notifyOldMgr} onChange={e => setNotifyOldMgr(e.target.checked)}
            disabled={!isEditing}>知會原任主管</Checkbox>
        </div>

        {/* Description */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">說明</label>
          <Input.TextArea value={desc} onChange={e => setDesc(e.target.value)}
            rows={2} disabled={!isEditing} />
        </div>

        {/* Detail grid */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">
              異動事由明細 <span className="text-red-500">*</span>
            </label>
            {isEditing && (
              <button className="sab-btn" onClick={addDetailRow}><PlusOutlined /> 新增明細</button>
            )}
          </div>
          <table className="w-full border text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="border px-2 py-1 w-12">序號</th>
                <th className="border px-2 py-1 w-40">薪資代號</th>
                <th className="border px-2 py-1">名稱</th>
                {isEditing && <th className="border px-2 py-1 w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {details.map((d, i) => (
                <tr key={i}>
                  <td className="border px-2 py-1 text-center">{i + 1}</td>
                  <td className="border px-1 py-1">
                    <Input value={d.f90f1_11} disabled={!isEditing} size="small"
                      onChange={e => updateDetailRow(i, 'f90f1_11', e.target.value)} />
                  </td>
                  <td className="border px-1 py-1">
                    <Input value={d.f90f1_12} disabled={!isEditing} size="small"
                      onChange={e => updateDetailRow(i, 'f90f1_12', e.target.value)} />
                  </td>
                  {isEditing && (
                    <td className="border px-1 py-1 text-center">
                      <button className="sab-btn sab-btn-danger" onClick={() => removeDetailRow(i)}>
                        <DeleteOutlined />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {details.length === 0 && (
                <tr><td colSpan={isEditing ? 4 : 3} className="border px-2 py-2 text-center text-gray-400">
                  無明細資料
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Salary adjustment details grid (F9441 - read-only) */}
        {current && (current.salary_details?.length ?? 0) > 0 && (
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 block mb-2">
              薪資異動明細
            </label>
            <table className="w-full border text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border px-2 py-1 w-12">序號</th>
                  <th className="border px-2 py-1 w-28">薪資代號</th>
                  <th className="border px-2 py-1 w-36">薪資名稱</th>
                  <th className="border px-2 py-1 w-28 text-right">原有金額</th>
                  <th className="border px-2 py-1 w-28 text-right">調整後金額</th>
                  <th className="border px-2 py-1 w-28 text-right">差額</th>
                </tr>
              </thead>
              <tbody>
                {current.salary_details!.map((sd, i) => {
                  const diff = (sd.f9441_12 || 0) - (sd.f9441_21 || 0);
                  return (
                    <tr key={i}>
                      <td className="border px-2 py-1 text-center">{sd.f9441_02}</td>
                      <td className="border px-2 py-1">{sd.f9441_11}</td>
                      <td className="border px-2 py-1">{sd.f9441_11a}</td>
                      <td className="border px-2 py-1 text-right">{(sd.f9441_21 || 0).toLocaleString()}</td>
                      <td className="border px-2 py-1 text-right">{(sd.f9441_12 || 0).toLocaleString()}</td>
                      <td className={`border px-2 py-1 text-right ${diff > 0 ? 'text-green-600' : diff < 0 ? 'text-red-600' : ''}`}>
                        {diff.toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Read-only info fields */}
        {current && mode === 'browse' && (
          <div className="grid grid-cols-3 gap-4 mb-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">異動事由明細筆數</label>
              <Input value={current.f90f_41 ?? 0} disabled style={{ width: 80 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">薪資異動明細筆數</label>
              <Input value={current.f90f_42 ?? 0} disabled style={{ width: 80 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">最後列印日期</label>
              <Input value={current.f90f_40 ? String(current.f90f_40).substring(0, 10) : ''} disabled style={{ width: 140 }} />
            </div>
          </div>
        )}

        {/* Page3: Approval section (read-only) */}
        {current && mode === 'browse' && (
          <div className="border-t pt-4 mb-4 mt-2">
            <h3 className="text-sm font-bold text-gray-600 mb-3">簽核資訊</h3>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">人事主管</label>
                <Input value={current.f90f_67 || ''} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">會簽日期</label>
                <Input value={current.f90f_68 ? String(current.f90f_68).substring(0, 10) : ''} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">核准說明</label>
                <Input.TextArea value={current.f90f_69 || ''} disabled rows={2} style={{ width: '100%' }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">副總核准</label>
                <Input value={current.f90f_72 || ''} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">核准日期</label>
                <Input value={current.f90f_73 ? String(current.f90f_73).substring(0, 10) : ''} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">核准說明</label>
                <Input.TextArea value={current.f90f_74 || ''} disabled rows={2} style={{ width: '100%' }} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">總經理代號</label>
                <Input value={current.f90f_77 || ''} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">核准日期</label>
                <Input value={current.f90f_78 ? String(current.f90f_78).substring(0, 10) : ''} disabled style={{ width: '100%' }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">核准說明</label>
                <Input.TextArea value={current.f90f_79 || ''} disabled rows={2} style={{ width: '100%' }} />
              </div>
            </div>
          </div>
        )}

        {/* Audit */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-4 space-y-1">
            <div>建立人員: {current.f90f_91} | 日期: {current.f90f_92 || '-'}</div>
            <div>修改人員: {current.f90f_93}</div>
          </div>
        )}
      </div>

      {/* SharedActionBar */}
      <SharedActionBar
        mode={mode} isEditing={isEditing} hasRecord={!!current}
        currentIdx={currentIdx} recordCount={records.length}
        onNavFirst={navFirst} onNavPrev={navPrev}
        onNavNext={navNext} onNavLast={navLast}
        onAdd={startAdd} onModify={startEdit} onDelete={handleDelete}
        onSave={save} onCancel={cancel}
        approval={approval}
        onSubmit={handleSubmit} onApprove={handleApprove}
        onReject={handleReject} onWriteoff={handleWriteoff} onHistory={() => setShowHistory(true)}
        onExit={() => navigate(-1)}
        canCreate={perm.canCreate} canEdit={perm.canEdit}
        canDelete={perm.canDelete} canApprove={perm.canApprove}
      />

      {/* Approval History Modal */}
      <ApprovalHistoryModal
        visible={showHistory}
        docNo={current?.f90f_01 || ''}
        onClose={() => setShowHistory(false)}
      />
    </Spin>
  );
};

export default PW9204F1Form;
